import { useState, useRef, useEffect } from 'react';

/**
 * SwipeableRow — touch-friendly action reveal for list rows.
 *
 * Props:
 *  - children: row content rendered on top of the action layer
 *  - rightActions: [{ icon, label, color, onAction, testId }] — revealed when user
 *      swipes the card to the LEFT (actions appear on the right edge). Typical
 *      destructive / secondary actions go here.
 *  - leftActions:  [{ icon, label, color, onAction, testId }] — revealed when user
 *      swipes the card to the RIGHT (actions appear on the left edge). Typical
 *      positive primary action ("Angekommen") goes here.
 *  - disabled: if true, swipe is disabled and the row behaves as a normal card.
 *
 * Behavior:
 *  - Snap-open when drag exceeds OPEN_THRESHOLD; otherwise spring back.
 *  - When open, tapping anywhere outside the action buttons (incl. the row body)
 *    closes the row again — so the normal onClick of children only fires when
 *    the row is closed.
 *  - Each action button is `Button-only` clickable; the gesture itself does
 *    NOT auto-fire to prevent accidental cancels.
 *
 * Visual: action area sized by button count × 84px.
 */
const ACTION_W = 84;
const OPEN_THRESHOLD = 32; // px of drag needed to snap open

export default function SwipeableRow({
  children,
  rightActions = [],
  leftActions = [],
  disabled = false,
  rowClassName = '',
  dataTestId,
}) {
  const [tx, setTx] = useState(0);              // current translateX
  const [open, setOpen] = useState(null);       // 'left' | 'right' | null
  const startX = useRef(0);
  const startTx = useRef(0);
  const dragging = useRef(false);
  const moved = useRef(false);                  // whether finger actually moved
  const rootRef = useRef(null);

  const rightW = rightActions.length * ACTION_W;
  const leftW = leftActions.length * ACTION_W;
  const minTx = -rightW;
  const maxTx = leftW;

  const close = () => { setTx(0); setOpen(null); };

  // Close when user taps outside this row
  useEffect(() => {
    if (!open) return;
    const onDocPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
  }, [open]);

  const onTouchStart = (e) => {
    if (disabled) return;
    dragging.current = true;
    moved.current = false;
    startX.current = e.touches[0].clientX;
    startTx.current = tx;
  };
  const onTouchMove = (e) => {
    if (disabled || !dragging.current) return;
    const dx = e.touches[0].clientX - startX.current;
    if (Math.abs(dx) > 4) moved.current = true;
    let next = startTx.current + dx;
    if (next < minTx) next = minTx;
    if (next > maxTx) next = maxTx;
    setTx(next);
  };
  const onTouchEnd = () => {
    if (disabled) return;
    dragging.current = false;
    if (tx <= -OPEN_THRESHOLD && rightActions.length) {
      setTx(-rightW); setOpen('right');
    } else if (tx >= OPEN_THRESHOLD && leftActions.length) {
      setTx(leftW); setOpen('left');
    } else {
      close();
    }
  };

  // Intercept clicks on the row body while open so we don't fire the underlying
  // onClick (e.g. opening the detail dialog) when the user just dragged.
  const onClickCapture = (e) => {
    if (open || moved.current) {
      e.stopPropagation();
      e.preventDefault();
      moved.current = false;
      if (open) close();
    }
  };

  const renderActions = (actions, side) => (
    <div
      className="absolute top-0 bottom-0 flex"
      style={{ [side]: 0 }}
      data-testid={`${dataTestId || 'swipe'}-actions-${side}`}
    >
      {actions.map((a, i) => (
        <button
          key={i}
          type="button"
          data-testid={a.testId}
          onClick={(e) => {
            e.stopPropagation();
            const fn = a.onAction;
            close();
            if (fn) fn();
          }}
          className="flex flex-col items-center justify-center gap-0.5 px-2 text-white text-[10px] font-medium uppercase tracking-wider active:brightness-90"
          style={{ width: ACTION_W, background: a.color || '#1C1C19' }}
        >
          {a.icon ? <a.icon className="w-4 h-4" /> : null}
          <span className="leading-tight text-center">{a.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`relative overflow-hidden ${rowClassName}`}
      data-testid={dataTestId}
    >
      {/* Action layers (rendered behind the row) */}
      {leftActions.length > 0 && renderActions(leftActions, 'left')}
      {rightActions.length > 0 && renderActions(rightActions, 'right')}

      {/* The row itself */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClickCapture={onClickCapture}
        style={{
          transform: `translateX(${tx}px)`,
          transition: dragging.current ? 'none' : 'transform 180ms ease-out',
          background: '#FFFFFF',
        }}
      >
        {children}
      </div>
    </div>
  );
}
